import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, ChevronDown, ChevronUp, Layers, Package, Cpu, Sliders } from 'lucide-react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { ASSUMPTION_META, FEATURES } from '../../data/conjointData';
import { formatNumber, formatPercent, formatPrice } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';
import { Slider } from '../common/Slider';
import type { Assumptions, PackageCostOverrides } from '../../types';

function formatAssumptionValue(key: keyof Assumptions, value: number): string {
  switch (key) {
    case 's1Leads': return formatNumber(value);
    case 'kitReturnRate':
    case 'consultRate':
    case 'totalDeductionRate':
    case 's2CloseRate':
      return formatPercent(value, 1);
    case 'fixedCosts':
    case 's2RepCost':
      return formatPrice(value);
    case 's1CostPerUnit': return '$' + value.toFixed(2);
    case 'featureScaling': return value.toFixed(1);
    default: return String(value);
  }
}

function formatOverrideValue(key: keyof PackageCostOverrides, value: number): string {
  switch (key) {
    case 'kitReturnRate':
    case 'consultRate':
    case 'totalDeductionRate':
    case 's2CloseRate':
      return formatPercent(value, 1);
    case 's2RepCost':
      return formatPrice(value);
    case 's1CostPerUnit': return '$' + value.toFixed(0);
    default: return String(value);
  }
}

const PORTFOLIO_ASSUMPTIONS: (keyof Assumptions)[] = [
  's1Leads', 'fixedCosts',
];

const PACKAGE_DEFAULT_KEYS: (keyof PackageCostOverrides)[] = [
  's1CostPerUnit', 's2CloseRate', 'totalDeductionRate', 's2RepCost', 'kitReturnRate', 'consultRate',
];

const MODEL_SETTINGS: (keyof Assumptions)[] = [
  'featureScaling',
];

interface SectionTheme {
  borderColor: string;
  bgColor: string;
  headerBg: string;
  iconColor: string;
  icon: React.ReactNode;
}

const SECTION_THEMES: Record<string, SectionTheme> = {
  portfolio: {
    borderColor: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.03)',
    headerBg: 'rgba(59, 130, 246, 0.08)',
    iconColor: '#3B82F6',
    icon: <Layers size={13} />,
  },
  packageDefaults: {
    borderColor: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.03)',
    headerBg: 'rgba(245, 158, 11, 0.08)',
    iconColor: '#F59E0B',
    icon: <Package size={13} />,
  },
  featureCogs: {
    borderColor: '#14B8A6',
    bgColor: 'rgba(20, 184, 166, 0.03)',
    headerBg: 'rgba(20, 184, 166, 0.08)',
    iconColor: '#14B8A6',
    icon: <Cpu size={13} />,
  },
  modelSettings: {
    borderColor: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.03)',
    headerBg: 'rgba(139, 92, 246, 0.08)',
    iconColor: '#8B5CF6',
    icon: <Sliders size={13} />,
  },
};

export function AssumptionsDrawer() {
  const [featureCostsOpen, setFeatureCostsOpen] = useState(false);
  const open = useSimulatorStore(s => s.assumptionsDrawerOpen);
  const close = () => useSimulatorStore.getState().setAssumptionsDrawerOpen(false);
  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const setAssumption = useSimulatorStore(s => s.setAssumption);
  const setFeatureCost = useSimulatorStore(s => s.setFeatureCost);
  const resetToDefaults = useSimulatorStore(s => s.resetToDefaults);

  const s1Acq = assumptions.s2CloseRate > 0 ? assumptions.s1CostPerUnit / assumptions.s2CloseRate : 0;
  const featureTotal = Object.values(assumptions.featureCosts || {}).reduce((s, v) => s + v, 0);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  function renderSliderGroup(keys: (keyof Assumptions)[]) {
    return keys.map(key => {
      const meta = ASSUMPTION_META[key];
      const value = assumptions[key] as number;
      return (
        <div key={key}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-secondary font-medium">{meta.label}</label>
              <InfoButton helpKey={key} size={12} />
            </div>
            <span className="font-mono text-sm text-primary font-semibold">
              {formatAssumptionValue(key, value)}
            </span>
          </div>
          <Slider
            value={value}
            min={meta.min}
            max={meta.max}
            step={meta.step}
            onChange={v => setAssumption(key, v)}
          />
          <div className="flex justify-between text-micro text-muted mt-1">
            <span>{formatAssumptionValue(key, meta.min)}</span>
            <span>{formatAssumptionValue(key, meta.max)}</span>
          </div>
        </div>
      );
    });
  }

  function renderPackageDefaultSlider(overrideKey: keyof PackageCostOverrides) {
    const assumptionKey = overrideKey as keyof Assumptions;
    const meta = ASSUMPTION_META[assumptionKey];
    const globalValue = assumptions[assumptionKey] as number;

    const usingGlobal: string[] = [];
    const usingCustom: { name: string; value: number }[] = [];

    packages.forEach(pkg => {
      const override = pkg.costOverrides?.[overrideKey];
      if (override !== undefined) {
        usingCustom.push({ name: pkg.name, value: override });
      } else {
        usingGlobal.push(pkg.name);
      }
    });

    return (
      <div key={overrideKey}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-secondary font-medium">{meta.label}</label>
            <InfoButton helpKey={overrideKey} size={12} />
          </div>
          <span className="font-mono text-sm text-primary font-semibold">
            {formatAssumptionValue(assumptionKey, globalValue)}
          </span>
        </div>
        <Slider
          value={globalValue}
          min={meta.min}
          max={meta.max}
          step={meta.step}
          onChange={v => setAssumption(assumptionKey, v)}
        />
        <div className="flex justify-between text-micro text-muted mt-1">
          <span>{formatAssumptionValue(assumptionKey, meta.min)}</span>
          <span>{formatAssumptionValue(assumptionKey, meta.max)}</span>
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          {usingGlobal.map(name => (
            <span key={name} className="text-micro px-1.5 py-0.5 rounded-full border border-border/60 text-muted">
              {name}
            </span>
          ))}
          {usingCustom.map(({ name, value }) => (
            <span key={name} className="text-micro px-1.5 py-0.5 rounded-full bg-chart-amber/15 text-chart-amber border border-chart-amber/30 font-medium">
              {name}: {formatOverrideValue(overrideKey, value)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  function SectionCard({ themeKey, title, subtitle, children }: { themeKey: string; title: string; subtitle?: string; children: React.ReactNode }) {
    const theme = SECTION_THEMES[themeKey];
    return (
      <div className="rounded-xl overflow-hidden" style={{ borderLeft: `3px solid ${theme.borderColor}`, backgroundColor: theme.bgColor }}>
        <div className="flex items-center gap-2 px-4 py-2.5" style={{ backgroundColor: theme.headerBg }}>
          <span style={{ color: theme.iconColor }}>{theme.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-detail uppercase tracking-wider font-bold" style={{ color: theme.iconColor }}>{title}</h3>
            {subtitle && <p className="text-micro text-muted leading-snug mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="px-4 py-4 space-y-5">
          {children}
        </div>
      </div>
    );
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90]">
          <motion.div
            className="absolute inset-0"
            style={{ backgroundColor: 'var(--color-backdrop)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          />

          <motion.aside
            className="absolute top-0 right-0 h-full w-[380px] max-w-[90vw] shadow-2xl overflow-y-auto flex flex-col"
            style={{ backgroundColor: 'var(--color-card)', borderLeft: '1px solid var(--color-border)' }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <div>
                <h2 className="text-base font-bold text-primary">Shared Assumptions</h2>
                <p className="text-xs text-muted mt-0.5">Parameters that feed every calculation across all tabs</p>
              </div>
              <button onClick={close} className="text-muted hover:text-primary transition-colors p-1" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 px-4 py-5 space-y-4">
              <div className="rounded-lg px-3 py-2 text-detail text-secondary leading-relaxed"
                   style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <span className="font-semibold text-primary">Source:</span>{' '}
                All defaults from the CFO&apos;s FEB 2026 Assumption Calculator.
                Net income per sale = Net revenue &minus; S1 acquisition &minus; S2 rep &minus; feature COGS.
              </div>

              {/* Portfolio-Level */}
              <SectionCard themeKey="portfolio" title="Portfolio-Level" subtitle="Apply to the entire business across all packages">
                {renderSliderGroup(PORTFOLIO_ASSUMPTIONS)}
              </SectionCard>

              {/* Package Defaults */}
              <SectionCard themeKey="packageDefaults" title="Package Defaults" subtitle="Defaults for packages without custom overrides. Amber badges = custom.">
                {PACKAGE_DEFAULT_KEYS.map(key => renderPackageDefaultSlider(key))}

                <div className="rounded-lg px-3 py-2 text-detail text-secondary"
                     style={{ backgroundColor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
                  Default S1 acq / S2 sale: <span className="font-mono font-semibold text-primary">{formatPrice(Math.round(s1Acq))}</span>
                  <span className="text-muted"> ({formatPrice(assumptions.s1CostPerUnit)} &divide; {formatPercent(assumptions.s2CloseRate, 0)} close = {(1/assumptions.s2CloseRate).toFixed(1)} S1s)</span>
                </div>
              </SectionCard>

              {/* Per-Feature COGS */}
              <SectionCard themeKey="featureCogs" title="Per-Feature COGS" subtitle="Cost of goods for each feature, shared across all packages">
                <button
                  onClick={() => setFeatureCostsOpen(!featureCostsOpen)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-xs text-secondary font-medium">
                    {featureCostsOpen ? 'Collapse' : 'Expand'} feature costs
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-detail text-muted">
                      {formatPrice(featureTotal)} total
                    </span>
                    {featureCostsOpen ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
                  </div>
                </button>

                {featureCostsOpen && (
                  <div className="space-y-4 mt-2">
                    {FEATURES.map(f => {
                      const cost = assumptions.featureCosts?.[f.name] ?? 0;
                      return (
                        <div key={f.name}>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-detail text-secondary truncate pr-2">{f.name}</label>
                            <span className="font-mono text-xs text-primary font-semibold">{formatPrice(cost)}</span>
                          </div>
                          <Slider
                            value={cost}
                            min={0}
                            max={300}
                            step={5}
                            onChange={v => setFeatureCost(f.name, v)}
                          />
                        </div>
                      );
                    })}
                    <div className="flex justify-between text-micro text-muted pt-1 border-t" style={{ borderColor: 'rgba(20, 184, 166, 0.2)' }}>
                      <span>Feature COGS subtotal</span>
                      <span className="font-mono font-semibold">{formatPrice(featureTotal)}</span>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Model Settings */}
              <SectionCard themeKey="modelSettings" title="Model Settings" subtitle="Analytical parameters that control how conjoint data is interpreted">
                {renderSliderGroup(MODEL_SETTINGS)}
              </SectionCard>
            </div>

            <div className="px-4 py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button
                onClick={() => { resetToDefaults(); }}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm text-secondary
                           rounded-lg transition-all hover:text-primary"
                style={{ border: '1px solid var(--color-border)' }}
              >
                <RotateCcw size={14} />
                Reset to CFO Defaults
              </button>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
